import microsoftlogo from 'assets/microsoftlogo.png';
import { FusionCustomInput } from 'components/index';
import BaseModal from 'components/Modals/BaseModal';
import BaseTable from 'components/Tables/BaseTable';
import Cookies from 'js-cookie';
import { useEffect, useMemo, useState } from 'react';
import { Button, Container, Form } from 'react-bootstrap';

const DetailsTab = ({
  formData,
  setFormData,
  membersList,
  setMembersList,
  roleid,
  edit = true,
  onClearForm = () => {},
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [toSelect, setToSelect] = useState([]);
  const [userList, setUserList] = useState([]);

  useEffect(() => {
    if (!membersList || membersList.length === 0) {
      const cachedList = JSON.parse(localStorage.getItem('membersList'));
      setUserList(cachedList || []);
      return;
    }

    const filteredList = membersList.filter((member) => member.selected === true);

    localStorage.setItem('membersList', JSON.stringify(filteredList));
    setUserList(filteredList);
  }, [membersList]);

  const openModal = () => {
    setToSelect(userList.map((user) => user.userId));
    setModalOpen(true);
  };

  const handleSubmit = () => {
    setMembersList((prev = []) =>
      prev.map((member) => ({
        ...member,
        selected: toSelect.includes(member.userId),
      })),
    );
    setModalOpen(false);
  };

  const handleRowClick = (row) => {
    // prevent toggling Microsoft users
    const isMicrosoftUser = row.disabled;
    if (isMicrosoftUser) return;

    if (toSelect.includes(row.id)) {
      setToSelect((prev) => prev.filter((id) => id !== row.id));
      return;
    }
    setToSelect((prev) => [...prev, row.id]);
  };

  const columns = useMemo(() => {
    return [
      {
        header: 'Users',
        accessorKey: 'name',
        Cell: ({ row, cell }) => {
          const name = cell.getValue();
          const activated = row.original.activated;
          const isMicrosoftUser = row.original.disabled;
          return (
            <p className="text-white fs-6 text-wrap text-break d-flex gap-2 align-items-center">
              {isMicrosoftUser && (
                <span>
                  <img src={microsoftlogo} style={{ height: 20 }} alt="Microsoft" />
                </span>
              )}
              {name}
              {activated === 0 && <span className="text-danger">(pending)</span>}
            </p>
          );
        },
        size: 180,
      },
      {
        header: 'Email',
        accessorKey: 'email',
        Cell: ({ row, cell }) => {
          const email = cell.getValue();
          return <p className="text-white fs-6 text-wrap text-break">{email}</p>;
        },
        size: 200,
      },
      {
        header: 'Created by',
        accessorKey: 'create_by_email',
        Cell: ({ row, cell }) => {
          const createdBy = cell.getValue();
          return <p className="text-white fs-6 text-wrap text-break">{createdBy}</p>;
        },
        size: 200,
      },
      {
        header: 'Date added',
        accessorKey: 'date_added',
        Cell: ({ row, cell }) => {
          const dateAdded = cell.getValue();
          return <p className="text-white fs-6 text-wrap text-break">{dateAdded}</p>;
        },
        size: 150,
      },
    ];
  }, [roleid]);

  const rows = useMemo(() => {
    if (!userList) return [];

    return userList?.map((user, index) => {
      return {
        id: user.userId,
        name: user.name,
        email: user.email,
        create_by_email: user.create_by_email,
        date_added: new Date(user.date_added).toLocaleString(),
        msRoles: user.msRoles,
        activated: user.activated,
        disabled: JSON.parse(user.msRoles).includes(Number(roleid)) ?? false,
      };
    });
  }, [userList]);

  const modalRows = useMemo(() => {
    if (!membersList) return [];

    return membersList
      ?.filter((user) => user.email !== Cookies.get('username'))
      .map((user, index) => {
        return {
          id: user.userId,
          name: user.name,
          email: user.email,
          create_by_email: user.create_by_email,
          date_added: new Date(user.date_added).toLocaleString(),
          msRoles: user.msRoles,
          activated: user.activated,
          index: index,
          disabled: JSON.parse(user.msRoles).includes(Number(roleid)) ?? false,
        };
      });
  }, [membersList]);

  return (
    <Container
      fluid
      className="d-flex flex-column w-100 gap-3 flex-grow-1"
      style={{ minHeight: 0 }}
    >
      <FusionCustomInput className="w-3xs" label="Role Name" disabled={!edit} required={true}>
        <Form.Control
          className="w-100 bg-transparent border-0"
          disabled={!edit}
          value={formData.roleName}
          onChange={(value) => setFormData({ ...formData, roleName: value.target.value })}
        />
      </FusionCustomInput>
      <div className="w-100 flex-grow-1" style={{ minHeight: 0 }}>
        <BaseTable
          rows={rows}
          columns={columns}
          customStyles={{
            cell: { borderBottom: '3px solid #E0E1E228' },
            header: { borderBottom: '3px solid #E0E1E228' },
            container: { height: '100%' },
          }}
          muiTablePaperProps={{ sx: { height: '100%', display: 'flex', flexDirection: 'column' } }}
          muiTableContainerProps={{ sx: { flex: 1, overflow: 'auto' } }}
        />
      </div>
      {edit && (
        <div className="d-flex flex-column flex-lg-row justify-content-between w-100 gap-2">
          <Button className="p-3 rounded-2 w-100 w-lg-3xs" onClick={onClearForm}>
            Clear Form
          </Button>
          <Button className="p-3 rounded-2 w-100 w-lg-3xs" onClick={() => openModal()}>
            Add/Remove User(s)
          </Button>
        </div>
      )}
      <BaseModal
        show={modalOpen}
        handleShow={() => setModalOpen(false)}
        size="xl"
        style={{ '--bs-modal-width': 'min(92vw, 1320px)' }}
        contentStyle={{ maxHeight: '82vh', minHeight: 0, overflow: 'hidden' }}
      >
        <div className="d-flex flex-column h-100" style={{ minHeight: 0 }}>
          <h1 className="fs-3 text-center text-sm-start flex-shrink-0 mb-2">
            Select User(s) to Add/Remove from Role
          </h1>
          <div className="flex-grow-1 overflow-hidden" style={{ minHeight: 0 }}>
            <BaseTable
              columns={columns}
              rows={modalRows}
              customStyles={{
                cell: { borderBottom: '3px solid #E0E1E228' },
                header: { borderBottom: '3px solid #E0E1E228' },
                container: { height: '100%' },
              }}
              muiTablePaperProps={{
                sx: { height: '100%', display: 'flex', flexDirection: 'column' },
              }}
              muiTableContainerProps={{ sx: { flex: 1, overflow: 'auto' } }}
              selectedRowIds={toSelect}
              onRowClick={handleRowClick}
            />
          </div>
          <div className="d-flex flex-column flex-lg-row w-100 mt-2 justify-content-between gap-2 flex-shrink-0">
            <Button
              className="py-3 rounded-2 border border-1 border-white w-100 w-lg-3xs"
              onClick={() => setModalOpen(false)}
              variant="dark"
            >
              Cancel
            </Button>
            <Button
              className="p-3 rounded-2 border border-1 border-white w-100 w-lg-3xs"
              onClick={() => handleSubmit()}
            >
              Submit
            </Button>
          </div>
        </div>
      </BaseModal>
    </Container>
  );
};

export default DetailsTab;
